import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useT } from "../lib/i18n";

/**
 * OperationsLedger
 * --------------------------------------------------
 * Quiet operational receipt of every submission the prospect has made
 * in the current session. NOT a notification center, NOT a chat
 * widget, NOT a live event dashboard.
 *
 * Strict design rules (per user directive):
 *   - Monochrome / dim
 *   - No glow container, no bright orange panel
 *   - Hidden until the first submission fires
 *   - Dismissible (X) and collapsible (chevron)
 *   - Mobile: collapsed by default
 *   - Max 4 events visible — older ones rotate out
 *   - Bottom-right · `position: fixed` · low z so it never competes
 *   - Subtle 200ms fade-in on the NEW line only · no panel motion
 *
 * Emotional target:
 *   "Everything I do leaves an operational footprint."
 *   NOT "wow this UI is busy."
 */

const LS_DISMISSED = "forgeops.ledger.dismissed.v1";
const LS_COLLAPSED = "forgeops.ledger.collapsed.v1";

function buildEntry(detail) {
  const kind = (detail?.kind || "").toLowerCase();
  if (kind === "toolbox-talk") {
    return {
      tone: "ok",
      label: "TT",
      copy:  `Toolbox Talk · ${detail?.attendees ?? 7} acks · routed in 12s`,
      detail: {
        title: "Toolbox Talk submitted",
        what: `${detail?.attendees ?? 7} crew members acknowledged the safety talk in the field`,
        routedTo: ["Safety Lead Console", "PM Folder", "Job Folder", "Operator File · per ack", "Compliance Archive"],
        artifacts: ["OSHA-ready PDF packet (EN + ES)", "Per-operator signature record"],
        dashboards: ["Compliance · Workforce Readiness % rolled forward", "Pressure Panel · Pending CAs reviewed"],
        notified: ["Safety Lead (push + SMS)", "PM (console badge)"],
      },
    };
  }
  if (kind === "incident" || kind === "near-miss") {
    return {
      tone: "critical",
      label: "INC",
      copy:  "Incident · escalated to safety lead",
      detail: {
        title: "Incident escalation",
        what: "Field-reported incident routed through safety + executive chain in <15s",
        routedTo: ["Safety Lead", "PM Console", "Executive Risk Dashboard", "Corrective Action Queue", "Training Center"],
        artifacts: ["Incident PDF + photos", "Corrective Action ticket (24h SLA)", "Training assignment (EN + ES)"],
        dashboards: ["Exec Risk Heat-Map · Safety score adjusted", "Pressure Panel · Open Incidents +1"],
        notified: ["Safety Lead (SMS · 12s)", "PM (console + push)", "Exec dashboard"],
      },
    };
  }
  if (kind === "daily-report") {
    return {
      tone: "info",
      label: "DR",
      copy:  "Daily Report · routed to PM · PDF rendered",
      detail: {
        title: "Daily Report routing",
        what: "Field report auto-routed to PM with attendance, production, and equipment time",
        routedTo: ["PM Console", "Executive KPI roll-up", "Job Folder", "Cost Coding", "Audit Archive"],
        artifacts: ["Branded Daily Report PDF", "Attendance roster", "Equipment hours log"],
        dashboards: ["Production · Hours · Cost rolled forward to Exec", "Pressure Panel · Overdue Approvals shifted"],
        notified: ["PM (console)", "Cost accountant (auto-tag)"],
      },
    };
  }
  if (kind === "preop-fail") {
    return {
      tone: "warn",
      label: "PRE-OP",
      copy:  "Pre-Op fail · unit locked out · shop notified",
      detail: {
        title: "Pre-Op failure lockout",
        what: "Operator flagged a defect — unit cannot dispatch until shop signs RTS with photos",
        routedTo: ["Shop Manager", "Dispatch (lockout flag)", "PM Console", "Fleet Log", "Job Cost"],
        artifacts: ["Pre-Op fail record + photos", "Shop repair ticket", "RTS sign-off (pending)"],
        dashboards: ["Exec · Equipment Risk score adjusted", "Pressure Panel · Equipment Lockouts +1"],
        notified: ["Shop Manager (push)", "Dispatcher (lockout banner)", "PM (console)"],
      },
    };
  }
  if (kind === "qaqc") {
    return {
      tone: "info",
      label: "QA",
      copy:  "QA / QC · audit trail logged",
      detail: {
        title: "QA / QC inspection",
        what: "Inspection record logged with pass/fail per checkpoint",
        routedTo: ["PM Console", "Job Folder", "Audit Archive", "Subcontractor file (if applicable)"],
        artifacts: ["Inspection PDF", "Pass/fail trail"],
        dashboards: ["Pressure Panel · Pending CAs adjusted if any failure"],
        notified: ["PM (console)"],
      },
    };
  }
  return null;
}

function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const TONE = {
  ok:       "#65DA76",
  warn:     "#FFB000",
  info:     "#FFFFFF",
  critical: "#FF6A6A",
};

const LABEL_TO_LONG = {
  TT: "Toolbox Talk",
  INC: "Incident",
  DR: "Daily Report",
  "PRE-OP": "Pre-Op fail",
  QA: "QA/QC inspection",
};

export default function OperationsLedger() {
  const { t } = useT();
  const [entries, setEntries] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [openId, setOpenId]   = useState(null);   // entry currently expanded

  // Detect mobile-ish to default collapsed
  useEffect(() => {
    try {
      if (sessionStorage.getItem(LS_DISMISSED) === "1") setDismissed(true);
      const stored = sessionStorage.getItem(LS_COLLAPSED);
      if (stored !== null) {
        setCollapsed(stored === "1");
      } else {
        setCollapsed(window.innerWidth < 768); // mobile collapsed by default
      }
    } catch { /* */ }
  }, []);

  useEffect(() => {
    const onSubmit = (ev) => {
      const e = buildEntry(ev?.detail || {});
      if (!e) return;
      setEntries((prev) =>
        [{ ...e, id: Date.now() + Math.random(), at: nowHHMM(), isNew: true }, ...prev].slice(0, 4)
      );
      // Clear isNew flag for the latest after 250ms so the fade only plays once
      setTimeout(() => {
        setEntries((prev) => prev.map((p, i) => (i === 0 ? { ...p, isNew: false } : p)));
      }, 260);
    };
    window.addEventListener("forgeops:submission", onSubmit);
    return () => window.removeEventListener("forgeops:submission", onSubmit);
  }, []);

  // Publish a session-stats snapshot for other modules (DemoExplainer
  // reseed-with-memory) to read at any time.
  useEffect(() => {
    try {
      const byLabel = {};
      entries.forEach((e) => {
        byLabel[e.label] = (byLabel[e.label] || 0) + 1;
      });
      window.__forgeopsLedgerStats = {
        total: entries.length,
        byLabel,
        readable: Object.entries(byLabel)
          .map(([k, v]) => `${v} ${LABEL_TO_LONG[k] || k}${v > 1 ? "s" : ""}`)
          .join(", "),
      };
    } catch { /* */ }
  }, [entries]);

  if (dismissed) return null;
  if (entries.length === 0) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(LS_DISMISSED, "1"); } catch { /* */ }
    setDismissed(true);
  };
  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const nxt = !v;
      try { sessionStorage.setItem(LS_COLLAPSED, nxt ? "1" : "0"); } catch { /* */ }
      return nxt;
    });
  };

  // Collapsed: tiny pill with count
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={toggleCollapsed}
        className="fixed bottom-4 right-4 z-30 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded font-mono text-[10px] uppercase tracking-[0.25em] font-bold print:hidden"
        style={{
          background: "#0A0A0A",
          color: "#888",
          border: "1px solid #1A1A1A",
          boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
        }}
        data-testid="ops-ledger-toggle"
        aria-label="Show operations ledger"
        title={`${entries.length} session events`}
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#FF6A00" }} />
        {entries.length} · log
        <ChevronUp className="w-3 h-3" />
      </button>
    );
  }

  return (
    <aside
      className="fixed bottom-4 right-4 z-30 w-[280px] max-w-[calc(100vw-2rem)] rounded print:hidden"
      style={{
        background: "#0A0A0A",
        border: "1px solid #1A1A1A",
        boxShadow: "0 6px 18px rgba(0,0,0,0.45)",
      }}
      data-testid="ops-ledger"
      aria-label="Session operations ledger"
    >
      <header
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ borderColor: "#161616" }}
      >
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] font-bold" style={{ color: "#888" }}>
          {t("Session ledger")}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={toggleCollapsed}
            className="inline-flex items-center justify-center w-5 h-5 rounded transition-colors"
            style={{ color: "#666" }}
            data-testid="ops-ledger-collapse"
            aria-label="Collapse ledger"
            title="Collapse"
          >
            <ChevronDown className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="inline-flex items-center justify-center w-5 h-5 rounded transition-colors"
            style={{ color: "#666" }}
            data-testid="ops-ledger-dismiss"
            aria-label="Dismiss ledger"
            title="Hide for this session"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </header>
      <ul className="px-3 py-2 space-y-1.5" data-testid="ops-ledger-list">
        {entries.map((e) => {
          const tone = TONE[e.tone] || "#888";
          return (
            <li
              key={e.id}
              className="flex items-start gap-2 font-mono text-[10px] leading-snug"
              style={{
                color: "#888",
                opacity: e.isNew ? 0 : 1,
                animation: e.isNew ? "fo-ledger-in 220ms ease-out forwards" : "none",
              }}
              data-testid="ops-ledger-entry"
            >
              <span style={{ color: "#444" }}>{e.at}</span>
              <span
                className="font-bold tracking-[0.18em]"
                style={{ color: tone }}
              >
                {e.label}
              </span>
              <span style={{ color: "#BDBDBD" }} className="flex-1 truncate">{e.copy}</span>
            </li>
          );
        })}
      </ul>
      <style>{`
        @keyframes fo-ledger-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="ops-ledger-entry"] { animation: none !important; opacity: 1 !important; }
        }
      `}</style>
    </aside>
  );
}

function DetailRow({ label, items, tone }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-1.5">
      <div className="font-mono text-[8px] uppercase tracking-[0.3em] font-bold" style={{ color: tone }}>
        {label}
      </div>
      <ul className="mt-0.5 space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="text-[10px] leading-snug flex gap-1" style={{ color: "#BDBDBD" }}>
            <span style={{ color: "#444" }}>·</span>
            <span className="flex-1">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
